import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { formatShopifyMoney } from '@theme/utilities';

let preservedFilterValue = null;
const localQuantities = new Map();
let isHandlingExternalCartUpdate = false;
let isHandlingCheckout = false;
function waitForFrames(count = 2) {
  return new Promise((resolve) => {
    let frameCount = 0;
    function frame() {
      frameCount++;
      if (frameCount >= count) {
        resolve(undefined);
      } else {
        requestAnimationFrame(frame);
      }
    }
    requestAnimationFrame(frame);
  });
}

function dispatchCartUpdateEvent(sections = {}) {
  document.dispatchEvent(
    new CartUpdateEvent({}, '', {
      source: 'bundle-picker',
      sections,
    })
  );
}

function updateQuantityInputAttributes(quantityInput, quantity) {
  quantityInput.value = quantity.toString();
  quantityInput.setAttribute('data-cart-quantity', quantity.toString());
  quantityInput.dataset.previousQuantity = quantity.toString();
}

function setButtonDisabled(button, disabled) {
  button.disabled = disabled;
  if (disabled) {
    button.setAttribute('aria-disabled', 'true');
  } else {
    button.removeAttribute('aria-disabled');
  }
}

function transitionItem(itemElement, toVisible) {
  const inlineDisplay = itemElement.style.display;
  const computedDisplay = window.getComputedStyle(itemElement).display;
  const isCurrentlyHidden = inlineDisplay === 'none' || computedDisplay === 'none';

  if (!toVisible && isCurrentlyHidden) {
    return;
  }

  if (isCurrentlyHidden && toVisible) {
    const currentStyle = itemElement.getAttribute('style');
    if (currentStyle && currentStyle.trim() === 'display: none;') {
      itemElement.removeAttribute('style');
    } else {
      itemElement.style.removeProperty('display');
    }
  }

  itemElement.style.display = 'flex';
  itemElement.style.visibility = 'visible';

  const originalTransition = itemElement.style.transition || '';
  itemElement.style.transition = 'none';

  if (toVisible) {
    itemElement.style.opacity = '0';
    itemElement.style.transform = 'scale(0.95)';
  } else {
    itemElement.style.opacity = '1';
    itemElement.style.transform = 'scale(1)';
  }

  void itemElement.offsetHeight;

  itemElement.style.transition = originalTransition;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (toVisible) {
        itemElement.style.opacity = '1';
        itemElement.style.transform = 'scale(1)';
        itemElement.addEventListener(
          'transitionend',
          function cleanup() {
            itemElement.removeEventListener('transitionend', cleanup);
            itemElement.style.removeProperty('opacity');
            itemElement.style.removeProperty('transform');
            itemElement.style.removeProperty('display');
            itemElement.style.removeProperty('visibility');
            itemElement.style.removeProperty('transition');
          },
          { once: true }
        );
      } else {
        itemElement.style.opacity = '0';
        itemElement.style.transform = 'scale(0.95)';
        const finalize = () => {
          itemElement.style.display = 'none';
          itemElement.style.visibility = 'hidden';
          itemElement.style.removeProperty('transition');
        };
        const onTransitionEnd = (event) => {
          if (!event || event.target === itemElement) {
            itemElement.removeEventListener('transitionend', onTransitionEnd);
            finalize();
          }
        };
        itemElement.addEventListener('transitionend', onTransitionEnd, { once: true });
        waitForFrames(25).then(finalize);
      }
    });
  });
}

function showItemSmooth(item) {
  transitionItem(item, true);
}

function hideItemSmooth(item) {
  transitionItem(item, false);
}

function getSectionsToUpdate() {
  const sections = new Set();

  const bundlePickerSection = document.querySelector('.bundle-picker-section')?.closest('[id^="shopify-section-"]');
  if (bundlePickerSection) {
    const sectionId = bundlePickerSection.id?.replace('shopify-section-', '');
    if (sectionId) sections.add(sectionId);
  }

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sections.add(item.dataset.sectionId);
    }
  });

  const cartIcon = document.querySelector('cart-icon-component');
  if (cartIcon instanceof HTMLElement && cartIcon.dataset.sectionId) {
    sections.add(cartIcon.dataset.sectionId);
  }

  const cartDrawer = document.querySelector('cart-drawer-component');
  if (cartDrawer) {
    const cartDrawerItems = cartDrawer.querySelector('cart-items-component');
    if (cartDrawerItems instanceof HTMLElement && cartDrawerItems.dataset.sectionId) {
      sections.add(cartDrawerItems.dataset.sectionId);
    }
  }

  return Array.from(sections);
}

async function refreshSections(preservedActiveItems = null) {
  const sectionsToUpdate = getSectionsToUpdate();
  if (sectionsToUpdate.length === 0) return;

  try {
    const response = await fetch(`${window.location.pathname}?sections=${sectionsToUpdate.join(',')}`);
    if (!response.ok) return;
    const data = await response.json();
    await updateSections(data, preservedActiveItems, false);
  } catch (error) {
    console.error('Failed to refresh sections:', error);
  }
}

async function refreshCartDrawerSections() {
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const refreshPromises = [];

  for (const cartItemsComponent of cartItemsComponents) {
    if (cartItemsComponent instanceof HTMLElement && cartItemsComponent.dataset.sectionId) {
      const sectionId = cartItemsComponent.dataset.sectionId;
      refreshPromises.push(
        sectionRenderer.renderSection(sectionId, { cache: false }).catch((error) => {
          console.error(`[Bundle Picker] Failed to refresh cart drawer section ${sectionId}:`, error);
        })
      );
    }
  }

  await Promise.all(refreshPromises);
}

function getActiveFilterValue() {
  const activeFilter = document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn--active');
  return activeFilter ? activeFilter.dataset.filterValue : null;
}

function applyFilter(filterValue, useTransitions = true) {
  if (!filterValue) return;

  const filterButtons = document.querySelectorAll('[data-bundle-filters] .bundle-picker__filter-btn');
  const productItems = document.querySelectorAll('[data-bundle-grid] .bundle-picker__item');
  const bundleGrid = document.querySelector('[data-bundle-grid]');
  const gridWrapper = document.querySelector('.bundle-picker__grid-wrapper');

  if (bundleGrid) {
    bundleGrid.style.removeProperty('display');
    bundleGrid.style.removeProperty('opacity');
  }
  if (gridWrapper) {
    gridWrapper.style.removeProperty('display');
    gridWrapper.style.removeProperty('opacity');
  }

  filterButtons.forEach((btn) => {
    const btnElement = /** @type {HTMLElement} */ (btn);
    btnElement.classList.toggle('bundle-picker__filter-btn--active', btnElement.dataset.filterValue === filterValue);
  });

  const itemsToShow = [];
  const itemsToHide = [];
  const itemsAlreadyVisible = [];

  productItems.forEach((item) => {
    const itemElement = /** @type {HTMLElement} */ (item);
    const itemBottlesCount = itemElement.dataset.bottlesCount || '';
    const shouldShow = filterValue === 'all' ? itemBottlesCount !== '' : itemBottlesCount === filterValue;

    const inlineDisplay = itemElement.style.display;
    const computedDisplay = window.getComputedStyle(itemElement).display;
    const isCurrentlyHidden = inlineDisplay === 'none' || computedDisplay === 'none';

    if (shouldShow) {
      if (isCurrentlyHidden) {
        itemsToShow.push(itemElement);
      } else {
        itemsAlreadyVisible.push(itemElement);
      }
    } else if (!isCurrentlyHidden) {
      itemsToHide.push(itemElement);
    }
  });

  if (useTransitions) {
    const transitionDuration = '0.4s';
    const transitionEasing = 'cubic-bezier(0.4, 0, 0.2, 1)';
    const transition = `opacity ${transitionDuration} ${transitionEasing}, transform ${transitionDuration} ${transitionEasing}`;

    let hiddenCount = 0;
    const totalToHide = itemsToHide.length;
    const allItemsToShow = [...itemsToShow, ...itemsAlreadyVisible];

    const showItems = () => {
      allItemsToShow.forEach((itemElement) => {
        itemElement.style.transition = 'none';
        itemElement.style.display = 'flex';
        itemElement.style.visibility = 'visible';
        itemElement.style.opacity = '0';
        itemElement.style.transform = 'scale(0.95)';
        void itemElement.offsetHeight;
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          allItemsToShow.forEach((itemElement) => {
            itemElement.style.transition = transition;
            itemElement.style.opacity = '1';
            itemElement.style.transform = 'scale(1)';

            const cleanup = () => {
              itemElement.removeEventListener('transitionend', cleanup);
              itemElement.style.removeProperty('opacity');
              itemElement.style.removeProperty('transform');
              itemElement.style.removeProperty('transition');
            };
            itemElement.addEventListener('transitionend', cleanup, { once: true });
          });
        });
      });
    };

    if (itemsToHide.length > 0) {
      itemsToHide.forEach((itemElement) => {
        itemElement.style.transition = transition;
        itemElement.style.opacity = '0';
        itemElement.style.transform = 'scale(0.95)';
        itemElement.style.pointerEvents = 'none';

        const hideItem = () => {
          itemElement.style.display = 'none';
          itemElement.style.visibility = 'hidden';
          itemElement.style.removeProperty('opacity');
          itemElement.style.removeProperty('transform');
          itemElement.style.removeProperty('transition');
          itemElement.style.removeProperty('pointer-events');

          hiddenCount++;
          if (hiddenCount === totalToHide) {
            showItems();
          }
        };

        const onTransitionEnd = (event) => {
          if (!event || event.target === itemElement) {
            itemElement.removeEventListener('transitionend', onTransitionEnd);
            hideItem();
          }
        };

        itemElement.addEventListener('transitionend', onTransitionEnd, { once: true });
      });
    } else {
      showItems();
    }
  } else {
    itemsToHide.forEach((itemElement) => {
      itemElement.style.display = 'none';
      itemElement.style.visibility = 'hidden';
      itemElement.style.removeProperty('opacity');
      itemElement.style.removeProperty('transform');
    });
    itemsToShow.forEach((itemElement) => {
      itemElement.style.removeProperty('display');
      itemElement.style.removeProperty('visibility');
      itemElement.style.removeProperty('opacity');
      itemElement.style.removeProperty('transform');
    });
  }
}

function restoreFilterState(filterValue) {
  applyFilter(filterValue, true);
}

function getActiveProductItems() {
  const activeItems = new Map();
  const productItems = document.querySelectorAll('[data-bundle-grid] .bundle-picker__item');

  productItems.forEach((item) => {
    const productId = item.dataset.productId;
    if (!productId) return;

    const quantitySelector = item.querySelector('quantity-selector-component');
    const variantId = quantitySelector?.dataset.variantId;
    if (variantId) {
      const quantity = localQuantities.get(variantId) || 0;
      if (quantity > 0) {
        activeItems.set(productId, quantity);
      }
    }
  });

  return activeItems;
}

function getActiveVariantQuantities() {
  const variantQuantities = new Map();
  const productItems = document.querySelectorAll('[data-bundle-grid] .bundle-picker__item');

  productItems.forEach((item) => {
    const quantitySelector = item.querySelector('quantity-selector-component');
    const variantId = quantitySelector?.dataset.variantId;
    if (variantId) {
      const quantity = localQuantities.get(variantId) || 0;
      if (quantity > 0) {
        variantQuantities.set(variantId, quantity);
      }
    }
  });

  return variantQuantities;
}

function restoreActiveState(activeItems) {
  if (!activeItems || activeItems.size === 0) return;

  const productItems = document.querySelectorAll('[data-bundle-grid] .bundle-picker__item');
  productItems.forEach((item) => {
    const productId = item.dataset.productId;
    if (productId && activeItems.has(productId)) {
      updateActiveStatus(item, activeItems.get(productId));
    }
  });
}

function restoreVariantQuantities(variantQuantities) {
  if (!variantQuantities || variantQuantities.size === 0) return;

  variantQuantities.forEach((quantity, variantId) => {
    localQuantities.set(variantId, quantity);
  });

  // Restore quantity inputs
  const productItems = document.querySelectorAll('[data-bundle-grid] .bundle-picker__item');
  productItems.forEach((item) => {
    const quantitySelector = item.querySelector('quantity-selector-component');
    if (!quantitySelector) return;

    const variantId = quantitySelector.dataset.variantId;
    if (variantId && variantQuantities.has(variantId)) {
      const quantity = variantQuantities.get(variantId);
      const quantityInput = quantitySelector.querySelector('input[type="number"]');

      if (quantityInput && quantity > 0) {
        updateQuantityInputAttributes(quantityInput, quantity);
        updateActiveStatus(item, quantity);

        const variantHasQuantity = quantitySelector.dataset.variantHasQuantity === 'true';
        updateButtonStates(quantitySelector, variantHasQuantity, quantity);
      }
    }
  });
}

async function updateSections(
  sections,
  preservedActiveItems = null,
  shouldDispatchEvents = true,
  skipGridUpdate = false
) {
  const activeFilterValue = preservedFilterValue || getActiveFilterValue();
  preservedFilterValue = activeFilterValue;
  const activeProductItems = preservedActiveItems || getActiveProductItems();
  const updatedSectionIds = [];
  const morphPromises = [];

  if (activeFilterValue && !skipGridUpdate) {
    const bundleGrid = document.querySelector('[data-bundle-grid]');
    if (bundleGrid) {
      const existingItems = bundleGrid.querySelectorAll('.bundle-picker__item');
      existingItems.forEach((item) => {
        const itemElement = /** @type {HTMLElement} */ (item);
        itemElement.style.display = 'none';
      });
    }
  }

  Object.keys(sections).forEach((sectionId) => {
    const sectionHTML = sections[sectionId];

    if (morphSection && sectionHTML) {
      morphPromises.push(morphSection(sectionId, sectionHTML));
      updatedSectionIds.push(sectionId);
    } else if (sectionHTML) {
      const sectionElement = document.getElementById(`shopify-section-${sectionId}`);
      if (sectionElement) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHTML, 'text/html');
        const newSection = doc.getElementById(`shopify-section-${sectionId}`);
        if (newSection) {
          sectionElement.innerHTML = newSection.innerHTML;
          updatedSectionIds.push(sectionId);
          if (activeFilterValue && !skipGridUpdate) {
            restoreFilterState(activeFilterValue);
          }
        }
      }
    }
  });

  if (updatedSectionIds.length > 0) {
    await waitForFrames(1);
    initBundlePickerFilters();
  }

  if (morphPromises.length > 0) {
    await Promise.all(morphPromises);
    await waitForFrames(1);
    if (updatedSectionIds.length > 0) {
      initBundlePickerFilters();
      if (activeFilterValue && !skipGridUpdate) {
        restoreFilterState(activeFilterValue);
      }
    }
  }

  if (updatedSectionIds.length > 0 && activeFilterValue && !skipGridUpdate) {
    initBundlePickerFilters();
    restoreFilterState(activeFilterValue);
  }

  await waitForFrames(1);
  await Promise.all(
    ['quantity-selector-component', 'cart-quantity-selector-component'].map((tagName) =>
      customElements.whenDefined(tagName)
    )
  );
  await waitForFrames(2);

  if (updatedSectionIds.length > 0 && !skipGridUpdate) {
    const currentFilterValue =
      activeFilterValue ||
      getActiveFilterValue() ||
      document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn--active')?.dataset.filterValue ||
      document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn')?.dataset.filterValue;

    if (currentFilterValue) {
      restoreFilterState(currentFilterValue);
      preservedFilterValue = currentFilterValue;
    }

    requestAnimationFrame(() => {
      if (activeProductItems?.size > 0) {
        restoreActiveState(activeProductItems);
      }
      waitForFrames(2).then(() => {
        initBundlePickerFilters();
        initQuantitySelectors();
        updateFooterTotal();
        updateFooterProgress();
        const finalFilterValue =
          preservedFilterValue ||
          getActiveFilterValue() ||
          document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn--active')?.dataset.filterValue;
        if (finalFilterValue) {
          restoreFilterState(finalFilterValue);
        }
      });
    });
  } else if (updatedSectionIds.length > 0 && skipGridUpdate) {
    initBundlePickerFilters();
    initQuantitySelectors();
  }

  if (shouldDispatchEvents) {
    await waitForFrames(2);

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { sections: sections || {}, source: 'bundle-picker' },
      })
    );

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const refreshedSectionIds = new Set(updatedSectionIds);

    for (const cartItemsComponent of cartItemsComponents) {
      if (cartItemsComponent instanceof HTMLElement && cartItemsComponent.dataset.sectionId) {
        const sectionId = cartItemsComponent.dataset.sectionId;
        if (!refreshedSectionIds.has(sectionId) && !sections?.[sectionId]) {
          try {
            await sectionRenderer.renderSection(sectionId, { cache: false });
          } catch (error) {
            console.error(`[Bundle Picker] Failed to refresh cart drawer section ${sectionId}:`, error);
          }
        }
      }
    }

    dispatchCartUpdateEvent(sections || {});
  }

  if (!skipGridUpdate && !isHandlingCheckout) {
    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent('bundle-picker:section-updated'));
    });
  }
}

function updateActiveStatus(productItem, quantity) {
  const isActive = quantity > 0;
  const wasActive = productItem.classList.contains('bundle-picker__item--active');

  if (isActive !== wasActive) {
    productItem.classList.toggle('bundle-picker__item--active', isActive);

    const productCard = productItem.querySelector('product-card');
    if (productCard) {
      productCard.classList.toggle('bundle-picker__product-card--active', isActive);
    }
  }
}

function updateButtonStates(quantitySelector, variantHasQuantity, currentQuantity) {
  const plusButton = /** @type {HTMLButtonElement | null} */ (quantitySelector.querySelector('.quantity-plus'));
  const minusButton = /** @type {HTMLButtonElement | null} */ (quantitySelector.querySelector('.quantity-minus'));
  const quantityInput = /** @type {HTMLInputElement | null} */ (quantitySelector.querySelector('input[type="number"]'));

  if (quantityInput) {
    setButtonDisabled(quantityInput, !variantHasQuantity);
  }

  if (plusButton) {
    if (!variantHasQuantity) {
      setButtonDisabled(plusButton, true);
    } else {
      const variantInventoryQuantity = parseInt(quantitySelector.dataset.variantInventoryQuantity || '0', 10);
      const variantInventoryManagement = quantitySelector.dataset.variantInventoryManagement || '';
      const isMaxReached =
        variantInventoryManagement === 'shopify' &&
        variantInventoryQuantity > 0 &&
        currentQuantity >= variantInventoryQuantity;
      setButtonDisabled(plusButton, isMaxReached);
    }
  }

  if (minusButton) {
    setButtonDisabled(minusButton, currentQuantity <= 0);
  }
}

function handleQuantityChange(quantitySelector, productItem) {
  const variantId = /** @type {string | undefined} */ (quantitySelector.dataset.variantId);
  const quantityInput = /** @type {HTMLInputElement | null} */ (quantitySelector.querySelector('input[type="number"]'));
  if (!variantId || !quantityInput) {
    return;
  }

  const currentQuantity = parseInt(quantityInput.value) || 0;
  const previousQuantity = parseInt(quantityInput.dataset.previousQuantity || '0');

  if (currentQuantity > 0) {
    localQuantities.set(variantId, currentQuantity);
  } else {
    localQuantities.delete(variantId);
  }

  updateActiveStatus(productItem, currentQuantity);
  const variantHasQuantity = quantitySelector.dataset.variantHasQuantity === 'true';
  updateButtonStates(quantitySelector, variantHasQuantity, currentQuantity);
  updateQuantityInputAttributes(quantityInput, currentQuantity);

  updateFooterTotal();
  updateFooterProgress();
}

function initBundlePickerFilters() {
  const filtersContainer = document.querySelector('[data-bundle-filters]');
  if (filtersContainer) {
    filtersContainer.classList.add('bundle-picker__filters--initialized');
  }

  const filterButtons = document.querySelectorAll('[data-bundle-filters] .bundle-picker__filter-btn');

  if (filterButtons.length === 0) return;

  filterButtons.forEach((button) => {
    button.addEventListener('click', function () {
      const filterValue = this.dataset.filterValue;
      preservedFilterValue = filterValue;
      applyFilter(filterValue, true);
    });
  });
}

function initQuantitySelectors() {
  const productItems = document.querySelectorAll('[data-bundle-grid] .bundle-picker__item');

  productItems.forEach((item) => {
    const quantitySelector = /** @type {HTMLElement | null} */ (item.querySelector('quantity-selector-component'));
    if (!quantitySelector) return;

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      quantitySelector.querySelector('input[type="number"]')
    );
    if (!quantityInput) return;

    const variantId = /** @type {string | undefined} */ (quantitySelector.dataset.variantId);

    const variantAvailable = quantitySelector.dataset.variantAvailable === 'true';
    const variantInventoryQuantity = parseInt(quantitySelector.dataset.variantInventoryQuantity || '0', 10);
    const variantInventoryPolicy = quantitySelector.dataset.variantInventoryPolicy || '';
    const variantInventoryManagement = quantitySelector.dataset.variantInventoryManagement || '';

    const variantHasQuantity =
      variantInventoryManagement === 'shopify'
        ? variantInventoryQuantity > 0 || variantInventoryPolicy === 'continue'
        : variantAvailable;

    quantitySelector.dataset.variantHasQuantity = variantHasQuantity.toString();
    quantityInput.min = '0';

    const initialQuantity = parseInt(quantityInput.value) || 0;
    updateActiveStatus(item, initialQuantity);
    updateButtonStates(quantitySelector, variantHasQuantity, initialQuantity);

    if (variantId) {
      const localQuantity = localQuantities.get(variantId) || 0;
      updateQuantityInputAttributes(quantityInput, localQuantity);
      updateActiveStatus(item, localQuantity);
      updateButtonStates(quantitySelector, variantHasQuantity, localQuantity);
    } else {
      updateQuantityInputAttributes(quantityInput, 0);
      updateActiveStatus(item, 0);
      updateButtonStates(quantitySelector, variantHasQuantity, 0);
    }

    quantitySelector.dataset.bundleItemId = item.dataset.productId || '';
    quantitySelector.classList.add('bundle-picker__quantity-selector--initialized');

    quantityInput.addEventListener('quantity-selector:update', function (event) {
      const newQuantity = event.detail?.quantity || parseInt(quantityInput.value) || 0;
      quantityInput.value = newQuantity.toString();
      handleQuantityChange(quantitySelector, item);
    });

    quantityInput.addEventListener('change', function () {
      handleQuantityChange(quantitySelector, item);
    });

    const plusButton = /** @type {HTMLElement | null} */ (quantitySelector.querySelector('.quantity-plus'));
    const minusButton = /** @type {HTMLElement | null} */ (quantitySelector.querySelector('.quantity-minus'));

    if (plusButton) {
      plusButton.addEventListener(
        'click',
        function (e) {
          e.stopImmediatePropagation();
          e.preventDefault();

          if (quantityInput.dataset.updating === 'true') {
            return;
          }

          const variantHasQuantity = quantitySelector.dataset.variantHasQuantity === 'true';
          if (!variantHasQuantity) {
            return;
          }

          const currentValue = parseInt(quantityInput.value) || 0;
          const variantInventoryQuantity = parseInt(quantitySelector.dataset.variantInventoryQuantity || '0', 10);
          const variantInventoryManagement = quantitySelector.dataset.variantInventoryManagement || '';

          let maxValue = null;
          if (variantInventoryManagement === 'shopify' && variantInventoryQuantity > 0) {
            maxValue = variantInventoryQuantity;
          } else {
            maxValue = quantityInput.max ? parseInt(quantityInput.max) : null;
          }

          const newValue = maxValue ? Math.min(maxValue, currentValue + 1) : currentValue + 1;

          if (maxValue && newValue >= maxValue && currentValue >= maxValue) {
            return;
          }

          quantityInput.value = newValue.toString();
          quantityInput.setAttribute('data-cart-quantity', newValue.toString());
          handleQuantityChange(quantitySelector, item);
        },
        true
      );
    }

    if (minusButton) {
      minusButton.addEventListener(
        'click',
        function (e) {
          e.stopImmediatePropagation();
          e.preventDefault();

          if (quantityInput.dataset.updating === 'true') {
            return;
          }

          const currentValue = parseInt(quantityInput.value) || 0;
          const newValue = Math.max(0, currentValue - 1);

          quantityInput.value = newValue.toString();
          quantityInput.setAttribute('data-cart-quantity', newValue.toString());
          handleQuantityChange(quantitySelector, item);
        },
        true
      );
    }
  });
}

function initBundlePicker() {
  initBundlePickerFilters();
  initQuantitySelectors();

  const checkoutButton = document.querySelector('.footer-checkout-button');
  if (checkoutButton) {
    checkoutButton.addEventListener('click', function (e) {
      e.preventDefault();
      handleCheckout();
    });
  }

  const activeFilterValue =
    getActiveFilterValue() ||
    document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn--active')?.dataset.filterValue ||
    document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn')?.dataset.filterValue;

  if (activeFilterValue) {
    preservedFilterValue = activeFilterValue;
    applyFilter(activeFilterValue, false);
  }

  updateFooterTotal();
  updateFooterProgress();

  const bundleGrid = document.querySelector('[data-bundle-grid]');
  if (bundleGrid && typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(() => {
      if (isHandlingExternalCartUpdate) return;

      const productItems = document.querySelectorAll('[data-bundle-grid] .bundle-picker__item');
      productItems.forEach((item) => {
        const quantitySelector = item.querySelector('quantity-selector-component');
        const quantityInput = quantitySelector?.querySelector('input[type="number"]');
        if (quantityInput) {
          const quantity = parseInt(quantityInput.value) || 0;
          if (quantity > 0 && !item.classList.contains('bundle-picker__item--active')) {
            updateActiveStatus(item, quantity);
          }
        }
      });

      const currentFilterValue =
        preservedFilterValue ||
        getActiveFilterValue() ||
        document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn--active')?.dataset.filterValue;
      if (currentFilterValue) {
        preservedFilterValue = currentFilterValue;
        applyFilter(currentFilterValue, false);
      }
    });

    observer.observe(bundleGrid, {
      childList: true,
      subtree: true,
    });
  }
}

function handleExternalCartUpdate(event) {
  if (!event || !event.detail) return;

  const source = event.detail.data?.source;
  if (source === 'bundle-picker') {
    return;
  }

  const sections = event.detail.data?.sections;
  if (!sections) return;

  const bundlePickerSectionId = Object.keys(sections).find((key) => key.includes('bundle_picker'));

  isHandlingExternalCartUpdate = true;

  if (bundlePickerSectionId && sections[bundlePickerSectionId]) {
    // Preserve variant quantities before updating sections (to maintain footer progress)
    const preservedVariantQuantities = getActiveVariantQuantities();
    const preservedActiveItems = getActiveProductItems();

    updateSections(
      { [bundlePickerSectionId]: sections[bundlePickerSectionId] },
      preservedActiveItems,
      false,
      true
    ).then(() => {
      waitForFrames(1).then(() => {
        initQuantitySelectors();
        // Restore variant quantities to maintain footer progress and local state
        if (preservedVariantQuantities && preservedVariantQuantities.size > 0) {
          restoreVariantQuantities(preservedVariantQuantities);
        }
        updateFooterTotal();
        updateFooterProgress();
        isHandlingExternalCartUpdate = false;
      });
    });
  } else {
    isHandlingExternalCartUpdate = false;
  }
}

function resetFooterImmediately() {
  const footer = document.querySelector('.bundle-picker-footer');
  if (!footer) return;

  const progressBar = footer.querySelector('.bundle-picker-footer__progress-bar');
  if (progressBar) {
    progressBar.style.width = '0%';
  }

  [1, 2, 3].forEach((goalNum) => {
    const goalElement = footer.querySelector(`.bundle-picker-footer__goal-${goalNum}`);
    if (!goalElement) return;

    goalElement.classList.remove('is-reached');

    const iconImg = goalElement.querySelector('.bundle-picker-footer__goal-icon-img');
    if (iconImg instanceof HTMLImageElement) {
      const iconInactive = goalElement.getAttribute('data-icon-inactive');
      const iconDefault = goalElement.getAttribute('data-icon-default');
      const newIconSrc = iconInactive || iconDefault;
      if (newIconSrc) {
        iconImg.src = newIconSrc;
      }
    }
  });

  const progressTextTop = footer.querySelector('.bundle-picker-footer__progress-text');
  const progressTextBottom = footer.querySelector('.bundle-picker-footer__progress-subtext');
  if (progressTextTop) {
    const startText = footer.getAttribute('data-stage-start-top') || '';
    if (startText) {
      progressTextTop.innerHTML = startText;
    }
  }
  if (progressTextBottom) {
    const startText = footer.getAttribute('data-stage-start-bottom') || '';
    if (startText) {
      progressTextBottom.innerHTML = startText;
    }
  }
}

if (document.readyState === 'loading') {
  if (document.querySelector('.bundle-picker-footer')) {
    resetFooterImmediately();
  }
  document.addEventListener('DOMContentLoaded', function () {
    resetFooterImmediately();
    initBundlePicker();
  });
} else {
  resetFooterImmediately();
  initBundlePicker();
}

if (typeof document.addEventListener !== 'undefined') {
  document.addEventListener(ThemeEvents.cartUpdate, handleExternalCartUpdate);

  document.addEventListener('shopify:section:load', function (event) {
    if (event.detail.sectionId && event.detail.sectionId.includes('bundle-picker')) {
      const currentFilter = preservedFilterValue || getActiveFilterValue();
      initBundlePicker();
      waitForFrames(3).then(() => {
        const activeFilterValue =
          currentFilter ||
          getActiveFilterValue() ||
          document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn--active')?.dataset.filterValue ||
          document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn')?.dataset.filterValue;
        if (activeFilterValue) {
          preservedFilterValue = activeFilterValue;
          applyFilter(activeFilterValue, true);
        }
        updateFooterTotal();
        updateFooterProgress();
      });
    }
  });

  document.addEventListener('bundle-picker:section-updated', function () {
    if (isHandlingExternalCartUpdate || isHandlingCheckout) return;

    waitForFrames(2).then(() => {
      const activeFilterValue =
        preservedFilterValue ||
        getActiveFilterValue() ||
        document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn--active')?.dataset.filterValue ||
        document.querySelector('[data-bundle-filters] .bundle-picker__filter-btn')?.dataset.filterValue;
      if (activeFilterValue) {
        preservedFilterValue = activeFilterValue;
        applyFilter(activeFilterValue, true);
      }
      updateFooterTotal();
      updateFooterProgress();
    });
  });
}

function calculateLocalItemCount() {
  let totalCount = 0;
  localQuantities.forEach((quantity) => {
    totalCount += quantity;
  });
  return totalCount;
}

function calculateLocalTotal() {
  let total = 0;
  const productItems = document.querySelectorAll('[data-bundle-grid] .bundle-picker__item');

  productItems.forEach((item) => {
    const quantitySelector = item.querySelector('quantity-selector-component');
    if (!quantitySelector) return;

    const variantId = quantitySelector.dataset.variantId;
    if (!variantId) return;

    const quantity = localQuantities.get(variantId) || 0;
    if (quantity > 0) {
      const price = parseFloat(quantitySelector.dataset.variantPrice || '0');
      total += price * quantity;
    }
  });

  return total;
}

function updateFooterTotal() {
  const totalElement = document.querySelector('.bundle-picker-footer__total');
  if (!totalElement) return;

  const total = calculateLocalTotal();
  const moneyFormat = totalElement.getAttribute('data-money-format') || '{{amount}}';
  const currency = totalElement.getAttribute('data-currency') || 'GBP';

  totalElement.textContent = formatShopifyMoney(total, moneyFormat, currency);
}

function updateFooterProgress() {
  const footer = /** @type {HTMLElement | null} */ (document.querySelector('.bundle-picker-footer'));
  if (!footer) return;

  const thresholdBasisAttr = footer.getAttribute('data-threshold-basis') || 'quantity';
  const thresholdBasis = thresholdBasisAttr === 'spend' ? 'spend' : 'quantity';
  const currentValue = thresholdBasis === 'spend' ? calculateLocalTotal() : calculateLocalItemCount();

  const goal1Threshold = parseFloat(footer.getAttribute('data-goal-1-threshold') || '0') || 0;
  const goal2Threshold = parseFloat(footer.getAttribute('data-goal-2-threshold') || '0') || 0;
  const goal3Threshold = parseFloat(footer.getAttribute('data-goal-3-threshold') || '0') || 0;

  let maxGoal = goal3Threshold;
  if (maxGoal === 0) maxGoal = goal2Threshold;
  if (maxGoal === 0) maxGoal = goal1Threshold;

  let progressPercentage = 0;
  if (maxGoal > 0) {
    progressPercentage = Math.min(100, Math.round((currentValue / maxGoal) * 100));
  }

  const progressBar = footer.querySelector('.bundle-picker-footer__progress-bar');
  if (progressBar) {
    progressBar.style.width = `${progressPercentage}%`;
  }

  let stage = 'start';
  if (goal3Threshold > 0 && currentValue >= goal3Threshold) {
    stage = 'complete';
  } else if (goal2Threshold > 0 && currentValue >= goal2Threshold) {
    stage = 'goal2';
  } else if (goal1Threshold > 0 && currentValue >= goal1Threshold) {
    stage = 'goal1';
  }

  const progressTextTop = footer.querySelector('.bundle-picker-footer__progress-text');
  const progressTextBottom = footer.querySelector('.bundle-picker-footer__progress-subtext');

  const isResetting = currentValue === 0;

  if (progressTextTop) {
    let attrName = 'data-stage-start-top';
    if (stage === 'goal1') attrName = 'data-stage-goal1-top';
    else if (stage === 'goal2') attrName = 'data-stage-goal2-top';
    else if (stage === 'complete') attrName = 'data-stage-complete-top';

    const text = footer.getAttribute(attrName) || '';
    if (text && progressTextTop.innerHTML !== text) {
      if (isResetting) {
        progressTextTop.innerHTML = text;
        progressTextTop.style.opacity = '1';
      } else {
        progressTextTop.style.opacity = '0';
        requestAnimationFrame(() => {
          progressTextTop.innerHTML = text;
          requestAnimationFrame(() => {
            progressTextTop.style.opacity = '1';
          });
        });
      }
    }
  }

  if (progressTextBottom) {
    let attrName = 'data-stage-start-bottom';
    if (stage === 'goal1') attrName = 'data-stage-goal1-bottom';
    else if (stage === 'goal2') attrName = 'data-stage-goal2-bottom';
    else if (stage === 'complete') attrName = 'data-stage-complete-bottom';

    const text = footer.getAttribute(attrName) || '';
    if (text && progressTextBottom.innerHTML !== text) {
      if (isResetting) {
        progressTextBottom.innerHTML = text;
        progressTextBottom.style.opacity = '1';
      } else {
        progressTextBottom.style.opacity = '0';
        requestAnimationFrame(() => {
          progressTextBottom.innerHTML = text;
          requestAnimationFrame(() => {
            progressTextBottom.style.opacity = '1';
          });
        });
      }
    }
  }

  [1, 2, 3].forEach((goalNum) => {
    const goalElement = /** @type {HTMLElement | null} */ (
      footer.querySelector(`.bundle-picker-footer__goal-${goalNum}`)
    );
    if (!goalElement) return;

    const threshold = parseFloat(goalElement.dataset.threshold || '0') || 0;
    const goalReached = threshold > 0 && currentValue >= threshold;

    if (goalReached) {
      goalElement.classList.add('is-reached');
    } else {
      goalElement.classList.remove('is-reached');
    }

    const iconImg = goalElement.querySelector('.bundle-picker-footer__goal-icon-img');
    if (iconImg instanceof HTMLImageElement) {
      const iconActive = goalElement.getAttribute('data-icon-active');
      const iconInactive = goalElement.getAttribute('data-icon-inactive');
      const iconDefault = goalElement.getAttribute('data-icon-default');

      let newIconSrc = '';
      if (goalReached && iconActive) {
        newIconSrc = iconActive;
      } else if (!goalReached && iconInactive) {
        newIconSrc = iconInactive;
      } else if (iconDefault) {
        newIconSrc = iconDefault;
      }

      if (newIconSrc) {
        const currentSrc = iconImg.src.split('?')[0].split('#')[0];
        const newSrcNormalized = newIconSrc.split('?')[0].split('#')[0];
        if (
          currentSrc !== newSrcNormalized &&
          !currentSrc.endsWith(newSrcNormalized) &&
          !newSrcNormalized.endsWith(currentSrc)
        ) {
          iconImg.src = newIconSrc;
        }
      }
    }
  });
}

async function handleCheckout() {
  const itemsToAdd = [];

  localQuantities.forEach((quantity, variantId) => {
    if (quantity > 0) {
      itemsToAdd.push({
        id: variantId.toString(),
        quantity: quantity,
      });
    }
  });

  if (itemsToAdd.length === 0) {
    return;
  }

  isHandlingCheckout = true;

  try {
    const sectionsToUpdate = getSectionsToUpdate();

    const cartAddUrl = Theme?.routes?.cart_add_url || '/cart/add.js';

    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: itemsToAdd,
        sections: sectionsToUpdate.length > 0 ? sectionsToUpdate.join(',') : undefined,
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error('Failed to add to cart: ' + errorText);
    }

    const data = await response.json();

    localQuantities.clear();
    resetQuantitySelectors();

    if (data.sections && sectionsToUpdate.length > 0) {
      await updateSections(data.sections, null, true);
    } else {
      await refreshCartDrawerSections();
      dispatchCartUpdateEvent({});
    }

    await waitForFrames(2);
    updateFooterTotal();
    updateFooterProgress();

    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  } catch (error) {
    console.error('[Bundle Picker] Failed to checkout:', error);
  } finally {
    isHandlingCheckout = false;
  }
}

function resetQuantitySelectors() {
  const productItems = document.querySelectorAll('[data-bundle-grid] .bundle-picker__item');

  productItems.forEach((item) => {
    const quantitySelector = item.querySelector('quantity-selector-component');
    if (!quantitySelector) return;

    const quantityInput = quantitySelector.querySelector('input[type="number"]');
    if (!quantityInput) return;

    const variantId = quantitySelector.dataset.variantId;
    if (variantId) {
      localQuantities.delete(variantId);
    }

    updateQuantityInputAttributes(quantityInput, 0);
    quantityInput.dataset.updating = 'false';
    updateActiveStatus(item, 0);

    const variantHasQuantity = quantitySelector.dataset.variantHasQuantity === 'true';
    updateButtonStates(quantitySelector, variantHasQuantity, 0);
  });
}
